import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton';
import Edit from '../../../assets/Edit';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApproveIbRequestApi, GetPlanListApi, DeleteTempPlanApi, GetActiveIbPlanList, DeletePlanForIBUserApi } from '../../../api/request/ib/ibapproved';
import LoadableButton from '../../../components/buttons/LoadableButton';
import toast from 'react-hot-toast';
import Delete from '../../../assets/Delete';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

const IbApproveList = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const { id, type } = useParams();
    const navigate = useNavigate();
    const [commissionData, setCommissionData] = useState({ mixConfigs: [] });
    const [isDeletePlanForIBUserConfirmModal, setIsDeletePlanForIBUserConfirmModal] = useState(false);
    const [isDeleteConfirmModal, setIsDeleteConfirmModal] = useState(false);
    const [selectedPlanForDelete, setSelectedPlanForDelete] = useState(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const handleDeleteClick = (record) => {
        setSelectedPlanForDelete(record);
        if (type === 'pending') {
            setIsDeleteConfirmModal(true);
        }
        if (type === 'approved') {
            setIsDeletePlanForIBUserConfirmModal(true);
        }
    };

    const handleDeletePlanForIBUser = async () => {
        if (!selectedPlanForDelete) return;
        try {
            setIsDeleteLoading(true);
            const { data } = await DeletePlanForIBUserApi({ userId: id, planId: selectedPlanForDelete.planId })
            if (data?.success) {
                toast.success(data?.message);
                setIsDeletePlanForIBUserConfirmModal(false);
                setSelectedPlanForDelete(null);
                getCommissionData();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete plan');
        } finally {
            setIsDeleteLoading(false);
        }
    }

    const handleDeleteConfirm = async () => {
        if (!selectedPlanForDelete) return;
        try {
            setIsDeleteLoading(true);
            const { data } = await DeleteTempPlanApi({ planId: selectedPlanForDelete.planId, userId: id });
            if (data?.success) {
                toast.success(data?.message);
                setIsDeleteConfirmModal(false);
                setSelectedPlanForDelete(null);
                getCommissionData();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete plan');
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const mixColumns = [
        {
            title: 'Plan Name',
            dataIndex: 'planName',
            key: 'planName',
            render: (text) => <span className="font-medium text-gray-800">{text}</span>
        },
        {
            title: 'Group',
            dataIndex: 'groupName',
            key: 'groupName',
            render: (text) => (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium">
                    {text}
                </span>
            )
        },
        {
            title: 'Commission',
            dataIndex: 'ownCommission',
            key: 'commission',
            render: (val) => <span className="font-semibold">${(val ?? 0)?.toFixed(2)}</span>
        },
        {
            title: 'Chain',
            dataIndex: 'chain',
            key: 'chain',
            render: (chain) => (
                <span className="text-sm text-gray-600">{chain?.length || 0} IB(s)</span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <div className="flex items-center justify-center gap-1">
                    <Link to={`/ib/ib_config/${type}/${id}/${record.planId}`} className="cursor-pointer">
                        <Edit color="#6B7280" size={16} />
                    </Link>
                    <button onClick={() => handleDeleteClick(record)} className="cursor-pointer">
                        <Delete size={16} />
                    </button>
                </div>
            )
        }
    ];

    const getCommissionData = async () => {
        try {
            setIsLoading(true);
            if (type === "pending") {
                const { data } = await GetPlanListApi(id);
                setCommissionData(data?.data)
            } else {
                const { data } = await GetActiveIbPlanList(id);
                setCommissionData(data?.data)
            }
        } catch (err) { }
        finally { setIsLoading(false) }
    }

    useEffect(() => {
        getCommissionData();
    }, [id])

    const approveIbRequest = async () => {
        try {
            setIsApproving(true);
            const params = { userId: id, isNewRecord: true, isApproved: true, comment: "" }
            const { data } = await ApproveIbRequestApi(params);
            if (data?.success) {
                toast.success(data?.message);
                navigate('/ib/ib_manager?filter=Approved')
            } else {
                toast.error(data?.message);
            }
        } catch (err) { }
        finally { setIsApproving(false) }
    }

    const prepareMixData = () => {
        return (commissionData?.mixConfigs || []).map(plan => ({
            ...plan,
            key: plan.planId,
        }));
    };

    const expandedRowRender = (record) => {
        const chain = record?.chain || [];
        return (
            <div className="px-4 py-3">
                <p className="text-sm font-medium text-[#2F2B3D] mb-3">Commission Chain ({chain.length} IB{chain.length !== 1 ? 's' : ''})</p>
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {chain.map((ib, index) => (
                        <div key={ib.ibUserId || index} className="flex items-center gap-3 px-3 py-2 border rounded-lg bg-white">
                            <div className="bg-primary text-white px-2.5 py-0.5 rounded text-xs font-medium min-w-[65px] text-center">
                                Level {index}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#2F2B3D] text-sm truncate">{ib.name}</p>
                                <p className="text-xs text-gray-500 truncate">{ib.email}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <span className="font-semibold text-sm text-[#2F2B3D]">${ib.commission?.toFixed(2)}</span>
                                <span className="text-gray-400 text-xs">/ lot</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="p-6 min-h-screen">
                {isLoading ? (
                    <div className="flex justify-center flex-col my-[10%]">
                        <Spin size="large" />
                        <p className="primary text-center mt-2">Loading...</p>
                    </div>
                ) : (
                    <div className="w-full mx-auto">
                        <div className="flex justify-between mb-6 items-center">
                            <div>
                                <p className="text-[#2F2B3D] text-lg font-semibold">Commission Plans</p>
                                <p className="text-sm text-gray-500">Manage commission configurations for this IB</p>
                            </div>
                            <div className="flex gap-2">
                                {type === "pending" && (
                                    <LoadableButton
                                        className="bg-green-600 text-white px-4 py-2 rounded-md"
                                        onClick={() => approveIbRequest()}
                                        isLoading={isApproving}
                                        disabled={isApproving}
                                        loadingLable="Approving..."
                                        lable="Approve IB Request"
                                    />
                                )}
                                <Link to={`/ib/ib_config/${type}/${id}/0`} className="bg-primary text-white px-4 py-2 rounded-md">
                                    Add New
                                </Link>
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                            <TableWithSkeleton
                                columns={mixColumns}
                                data={prepareMixData()}
                                loading={false}
                                showIndexColumn={true}
                                pagination={false}
                                rowKey={(record) => record.key}
                                expandable={{
                                    expandedRowRender,
                                    expandRowByClick: true,
                                    expandedRowKeys,
                                    onExpand: (expanded, record) => {
                                        setExpandedRowKeys(expanded ? [record.key] : []);
                                    },
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={isDeleteConfirmModal}
                setIsOpen={setIsDeleteConfirmModal}
                type="error"
                message={`Are you sure you want to delete "${selectedPlanForDelete?.planName || 'this plan'}"?`}
                onConfirm={handleDeleteConfirm}
                isLoading={isDeleteLoading}
                loadingLabel="Deleting plan..."
            />

            <ConfirmationModal
                isOpen={isDeletePlanForIBUserConfirmModal}
                setIsOpen={setIsDeletePlanForIBUserConfirmModal}
                type="error"
                message={`Are you sure you want to delete "${selectedPlanForDelete?.planName || 'this plan'}"? All associated plans will also be deleted.`}
                onConfirm={handleDeletePlanForIBUser}
                isLoading={isDeleteLoading}
                loadingLabel="Deleting plan..."
            />
        </>
    );
}

export default IbApproveList;
